import React, {useState} from 'react'
import Grid from '@mui/material/Grid';
import Card from '@mui/material/Card';
import { CardActions } from '@mui/material';
import Typography from '@mui/material/Typography';
import CardContent from '@mui/material/CardContent';
import InputLabel from '@mui/material/InputLabel';
import FormControl from '@mui/material/FormControl';
import OutlinedInput from '@mui/material/OutlinedInput';
import { Button } from '@mui/material';
import { useMutation } from "react-query";
import apiClient from '../../request/http-common';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import { useQuery } from 'react-query';
import { useParams } from 'react-router-dom';
import Alert from '@mui/material/Alert';

const EditInvestment = () => {

    const params = useParams();

    const [investment, setInvestment] = useState(null);
    const [loadError, setLoadError] = useState('');
    const [investmentPlans, setInvestmentPlans] = useState(null);

    const { isLoading: isLoadingInvestment } = useQuery(
        ["get-investment", params.id],
        async () => {
            return await apiClient.get(`/api/get-investment/${params.id}`);
        },
        {
            enabled: !!params.id,
            onSuccess: (res) => {
                if (res.data.status === 'success') {
                    const inv = res.data.investment;
                    setInvestment(inv);
                    setInvestmentForm({
                        amount_invested: inv.amount_invested,
                        payment_source: inv.payment_source,
                        status: inv.status,
                        earning: inv.earning,
                        duration: inv.duration,
                        investment_plan_id: inv.investment_plan_id,
                    });
                }
            },
            onError: () => {
                setLoadError('Failed to load investment. Please go back and try again.');
            },
        }
    );

    useQuery(
        "investment-plans",
        async () => {
          return await apiClient.get(`/api/investment-plans`);
        },
        {
          enabled: true,
          onSuccess: (res) => {
            if (res.data.status === 'success') {
                setInvestmentPlans(res.data.plans);
            }
          },
        }
    );

    const [errors, setErrors] = useState({});
    const [generalError, setGeneralError] = useState('');
    const [investmentForm, setInvestmentForm] = useState({
        amount_invested: '',
        payment_source: '',
        status: '',
        earning: '',
        duration: '',
        investment_plan_id: '',
    });

    const onChange = (e) =>
        setInvestmentForm({ ...investmentForm, [e.target.name]: e.target.value });

    const { isLoading: isSendingRequest, mutate: postInvestment } = useMutation(
        async () => {
          return await apiClient.post(`/api/edit-investment/${params.id}`, {
            amount: investmentForm.amount_invested,
            payment_source: investmentForm.payment_source,
            status: investmentForm.status,
            earning: investmentForm.earning,
            duration: investmentForm.duration,
            investment_plan_id: investmentForm.investment_plan_id,
          });
        },
        {
          onSuccess: (res) => {
            setErrors({});
            setGeneralError('');
            localStorage.setItem('notification', JSON.stringify({message: `${res.data.message}`, type: 'success'}));
            const event = new Event('newMessage');
            window.dispatchEvent(event);
            const event2 = new Event('processed');
            window.dispatchEvent(event2);
          },
          onError: (err) => {
            const event2 = new Event('processed');
            window.dispatchEvent(event2);

            const data = err.response?.data;
            if (data?.errors) {
                setErrors(data.errors);
                setGeneralError('');
            } else if (data?.message) {
                setGeneralError(data.message);
                setErrors({});
            } else {
                setGeneralError('An unexpected error occurred. Please try again.');
                setErrors({});
            }
          },
        }
    );

    const onSubmit = () => {
        setErrors({});
        setGeneralError('');
        const event3 = new Event('processing');
        window.dispatchEvent(event3);
        postInvestment();
    };

    if (isLoadingInvestment) {
        return (
            <Box sx={{display: 'flex', justifyContent: 'center', alignItems: 'center', marginTop: '120px'}}>
                <CircularProgress />
            </Box>
        );
    }

    if (loadError) {
        return (
            <Box sx={{marginTop: '100px', px: 3}}>
                <Alert severity="error">{loadError}</Alert>
            </Box>
        );
    }

    if (!investment) return null;

  return (
    <div style={{marginTop: '70px'}}>
        <Grid container px={2} mt={4} mb={4} rowSpacing={1} columnSpacing={{ xs: 1, sm: 1, md: 1 }}>
            <Grid item xs={12} md={12}>
                <Card sx={{marginX: '5px', borderRadius: '9px', height: '100%'}}>
                    <CardContent>
                    <Typography mt={4} sx={{ fontWeight: 'bold', fontSize: 24, textAlign: 'center' }} gutterBottom variant="h6" component="div">
                        EDIT INVESTMENT
                    </Typography>
                    <Typography sx={{ textAlign: 'center', fontSize: 16 }} variant="subtitle1" gutterBottom>
                        Fill the form below to edit investment
                    </Typography>

                    {generalError !== '' &&
                        <Alert severity="error" sx={{mb: 2}}>{generalError}</Alert>
                    }

                    <FormControl sx={{ m: 1, minWidth: '100%' }} size="small">
                        <InputLabel htmlFor="payment_source">Select Payment Source</InputLabel>
                        <Select
                            onChange={onChange}
                            labelId="payment_source"
                            id="payment_source"
                            value={investmentForm.payment_source}
                            name="payment_source"
                            label="Select Payment Source"
                            error={!!errors.payment_source}
                        >
                            <MenuItem value=''>Select Gateway</MenuItem>
                            <MenuItem value="Overall Asset">Overall Asset</MenuItem>
                            <MenuItem value="Accumulated Profit">Accumulated Profit</MenuItem>
                        </Select>
                        {errors.payment_source &&
                            <Typography color='error' sx={{ textAlign: 'left', fontSize: 13 }} variant="subtitle1">
                                {errors.payment_source[0]}
                            </Typography>
                        }
                    </FormControl>

                    <FormControl fullWidth={true} sx={{ m: 1, minWidth: 50 }}>
                        <InputLabel htmlFor="amount_invested">Amount Invested</InputLabel>
                        <OutlinedInput
                            onChange={onChange}
                            error={!!errors.amount}
                            id="amount_invested"
                            value={investmentForm.amount_invested}
                            type="number"
                            name="amount_invested"
                            label="Amount Invested"
                        />
                        {errors.amount &&
                            <Typography color='error' sx={{ textAlign: 'left', fontSize: 13 }} variant="subtitle1">
                                {errors.amount[0]}
                            </Typography>
                        }
                    </FormControl>

                    <FormControl fullWidth={true} sx={{ m: 1, minWidth: 50 }}>
                        <InputLabel htmlFor="earning">Earning</InputLabel>
                        <OutlinedInput
                            onChange={onChange}
                            error={!!errors.earning}
                            value={investmentForm.earning}
                            id="earning"
                            type="number"
                            name="earning"
                            label="Earning"
                        />
                        {errors.earning &&
                            <Typography color='error' sx={{ textAlign: 'left', fontSize: 13 }} variant="subtitle1">
                                {errors.earning[0]}
                            </Typography>
                        }
                    </FormControl>

                    <FormControl fullWidth={true} sx={{ m: 1, minWidth: 50 }}>
                        <InputLabel htmlFor="duration">Duration</InputLabel>
                        <OutlinedInput
                            onChange={onChange}
                            error={!!errors.duration}
                            id="duration"
                            value={investmentForm.duration}
                            type="text"
                            name="duration"
                            label="Duration"
                        />
                        {errors.duration &&
                            <Typography color='error' sx={{ textAlign: 'left', fontSize: 13 }} variant="subtitle1">
                                {errors.duration[0]}
                            </Typography>
                        }
                    </FormControl>

                    {investmentPlans !== null &&
                        <FormControl sx={{ m: 1, minWidth: '100%' }} size="small">
                            <InputLabel htmlFor="investment_plan_id">Investment Plan</InputLabel>
                            <Select
                                onChange={onChange}
                                value={investmentForm.investment_plan_id}
                                labelId="investment_plan_id"
                                id="investment_plan_id"
                                name="investment_plan_id"
                                label="Investment Plan"
                                error={!!errors.investment_plan_id}
                            >
                                <MenuItem value="">Investment Plan</MenuItem>
                                {investmentPlans.map(plan => (
                                    <MenuItem key={plan.id} value={plan.id}>{plan.name}</MenuItem>
                                ))}
                            </Select>
                            {errors.investment_plan_id &&
                                <Typography color='error' sx={{ textAlign: 'left', fontSize: 13 }} variant="subtitle1">
                                    {errors.investment_plan_id[0]}
                                </Typography>
                            }
                        </FormControl>
                    }

                    <FormControl fullWidth={true} sx={{ m: 1, minWidth: 50 }}>
                        <InputLabel htmlFor="status">Status</InputLabel>
                        <Select
                            onChange={onChange}
                            value={investmentForm.status}
                            labelId="status"
                            id="status"
                            name="status"
                            label="Status"
                            error={!!errors.status}
                        >
                            <MenuItem value=''>Select Status</MenuItem>
                            <MenuItem value="pending">Pending</MenuItem>
                            <MenuItem value="approved">Approved</MenuItem>
                            <MenuItem value="declined">Declined</MenuItem>
                            <MenuItem value="completed">Completed</MenuItem>
                        </Select>
                        {errors.status &&
                            <Typography color='error' sx={{ textAlign: 'left', fontSize: 13 }} variant="subtitle1">
                                {errors.status[0]}
                            </Typography>
                        }
                    </FormControl>

                    </CardContent>

                    <CardActions>
                        <Button onClick={onSubmit} fullWidth={true} size="large" color="primary" variant="contained" disabled={isSendingRequest}>
                            {isSendingRequest ? 'Updating...' : 'Update investment'}
                        </Button>
                    </CardActions>
                </Card>
            </Grid>
        </Grid>
    </div>
  )
}

export default EditInvestment
